import type { Capability } from "@steelbill/types";
import { routes } from "@/routes/paths";

/**
 * THE SETTINGS AREA — ONE SIDEBAR ROW, FIVE DESTINATIONS, ONE LIST (§2.1).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THIS REPLACED, AND WHAT IT DELIBERATELY DID NOT CHANGE.
 *
 * The foot of the rail carried FOUR rows — Settings, Features, People, Activity
 * log — which is four of the seven rows a reader has, for the part of the app he
 * opens least. They are one row now, with the destinations behind it in a
 * navigation menu across the top of the area.
 *
 * NOT ONE URL MOVED. `/settings`, `/settings/features`, `/settings/users`,
 * `/settings/audit` and `/invoices/numbers` are exactly what they were, each
 * still guarded by its own capability in `routes/index.tsx`. A bookmark to the
 * audit window still opens the audit window, the back button still steps back
 * through the sections, and middle-click still opens one in a new tab — because
 * every item below is a LINK TO A ROUTE and not a panel swapped inside one
 * document. (That is also why the menu is `NavigationMenu` and not `Tabs`: a
 * tablist tells a screen reader the panels live on this page, which would be a
 * lie about five separate URLs behind five separate capability guards.)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY SECTION NAMES THE CAPABILITY ITS OWN ROUTE AND ITS OWN API ENFORCE, and
 * that is the whole reason this list exists rather than a hard-coded menu:
 *
 *   Business details  `settings.write`      PUT /company        (routes/company.ts)
 *   Bill numbers      `invoice.set_counter` PUT /company/counters (§6.1)
 *   Features          `settings.write`      the whole /flags router, read included
 *   People            `user.manage`         every /users route
 *   Activity log      `audit.view`          GET /audit
 *
 * §6.1'S COUNTER OVERRIDE IS THE ONE THAT IS NOT `settings.write`, AND IT IS THE
 * REASON THIS FILE IS CAREFUL. `settings.write` also governs the GSTIN, the PAN
 * and the bank details, which §2 says the OWNER has no business editing — while
 * he is exactly the person who writes bills by hand and has to nudge the series
 * afterwards. Folding the counters into the settings gate once made the screen
 * unreachable for the only person it was built for, and cost an afternoon.
 * Nothing here may re-collapse those two.
 * ─────────────────────────────────────────────────────────────────────────────
 * ═════════════════════════════════════════════════════════════════════════════
 */

/** One destination inside the settings area, and what it takes to open it. */
export interface SettingsSection {
  /** An i18n key. Every one of these already exists — no new strings. */
  titleKey: string;
  /** The real route. Never a panel id; see the header. */
  url: string;
  /** Exactly the capability the route guard and the server ask for. */
  capability: Capability;
}

/**
 * The sections, in menu order: who you are → what the next bill is numbered →
 * what is switched on → who may sign in → what was done.
 *
 * THE LABELS REUSE KEYS THAT ALREADY EXIST. `nav.features`, `nav.users` and
 * `nav.audit` are the strings the four old rows carried. The first two take the
 * SCREEN's own title instead of `nav.settings`, because inside a menu called
 * Settings an item called "Settings" names nothing — `settings.businessTitle`
 * ("Business details") and `counters.title` ("Document numbers") are
 * the words already printed at the top of those two screens, in both languages.
 */
export const SETTINGS_SECTIONS: readonly SettingsSection[] = Object.freeze([
  { titleKey: "settings.businessTitle", url: routes.settings, capability: "settings.write" },
  /*
   * §6.1, AND ITS URL IS NOT UNDER `/settings/` — that is not an oversight to be
   * tidied up. `/invoices/numbers` is what this screen is to the owner: the
   * number the next bill will get. It appears in this menu because it is plainly
   * part of configuring the system for whoever holds the capability, and the
   * screen renders inside this area so following the item is not a one-way door.
   */
  { titleKey: "counters.title", url: routes.invoiceNumbers, capability: "invoice.set_counter" },
  /*
   * §18.1 — the kill switches. The old comment on this row argued it should be a
   * row of its own rather than a card inside Settings, because "a switch that can
   * take billing down for everyone should not be reached by scrolling past the
   * bank details". THAT CONCERN IS STILL MET, and by a stronger mechanism: as its
   * own nav item it is ONE CLICK from anywhere in the area and not a scroll
   * through the company form at all. It also keeps `flags.moduleOffHelp` able to
   * name a screen the reader can actually reach
   * (components/__tests__/feature-off-copy.test.ts).
   */
  { titleKey: "nav.features", url: routes.features, capability: "settings.write" },
  { titleKey: "nav.users", url: routes.users, capability: "user.manage" },
  { titleKey: "nav.audit", url: routes.audit, capability: "audit.view" },
] as const);

/**
 * WHAT MAKES THE SETTINGS AREA YOURS — any ONE of these three, never all of
 * them, and never `settings.write` alone.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * GATING THE ONE SURVIVING ROW ON `settings.write` WOULD HAVE BEEN SHORTER AND
 * WOULD HAVE BEEN THE §6.1 MISTAKE IN A NEW COSTUME. §2.1's Administration row
 * lists `user.manage` and `audit.view` as capabilities IN THEIR OWN RIGHT; they
 * coincide with `settings.write` only because ADMIN happens to hold all three
 * today. A future role granted `user.manage` alone would pass the People route's
 * own gate and never be shown the door to it — invisible but reachable, which is
 * the same failure as visible but forbidden and harder to notice.
 *
 * `invoice.set_counter` IS DELIBERATELY NOT IN THIS LIST, although it is a
 * section above. Holding it is not a reason to be shown an ADMINISTRATION row:
 * the OWNER holds it and none of the other three, his counters screen lives
 * under `/invoices/` because that is what it is to him, and he reaches it from
 * the register and the search palette exactly as before. Adding it here would
 * put a "Settings" row in front of the one person §2 keeps out of settings, and
 * it would lead him to a menu of one item.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const SETTINGS_AREA_CAPABILITIES: readonly Capability[] = Object.freeze([
  "settings.write",
  "user.manage",
  "audit.view",
]);

/**
 * Where `/settings` sends somebody who cannot open the company form.
 *
 * DERIVED FROM {@link SETTINGS_SECTIONS} RATHER THAN WRITTEN OUT AGAIN, so a
 * section that is renamed or re-gated cannot land in one list and not the other.
 * The filter is what keeps §6.1's counters out of it: a capability that does not
 * make the area yours cannot be the reason you are dropped into it.
 */
export const SETTINGS_LANDING_ORDER: readonly SettingsSection[] = Object.freeze(
  SETTINGS_SECTIONS.filter((section) => SETTINGS_AREA_CAPABILITIES.includes(section.capability)),
);

/** `can` is the predicate from `useAuth()` — the same `can()` the server calls. */
export type CanFn = (capability: Capability) => boolean;

/**
 * The sections this reader may actually open, in menu order.
 *
 * Deny by default: a section whose capability is absent is NOT RENDERED, never
 * rendered disabled. A menu item that answers 403 turns a permission boundary
 * into something discovered after the tap.
 */
export function visibleSettingsSections(can: CanFn): readonly SettingsSection[] {
  return SETTINGS_SECTIONS.filter((section) => can(section.capability));
}

/** Does the settings area belong to this reader at all? (The sidebar row.) */
export function ownsSettingsArea(can: CanFn): boolean {
  return SETTINGS_AREA_CAPABILITIES.some((capability) => can(capability));
}

/**
 * The first section this reader can actually open, or `null` for nobody's.
 *
 * `/settings` uses it to land somebody who holds `user.manage` on People rather
 * than on a company form that would only bounce them Home — the same shape as
 * `routes/landing.ts` and for the same reason. It can never return `/settings`
 * itself unless `settings.write` is held, which is the case that renders rather
 * than redirects, so no loop is expressible.
 */
export function firstSettingsSection(can: CanFn): string | null {
  return SETTINGS_LANDING_ORDER.find((section) => can(section.capability))?.url ?? null;
}
